import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  notification,
  Space,
  Upload,
  Tag,
  Popconfirm
} from "antd";
import {CloseOutlined, FilePdfOutlined, FileExcelOutlined, CloseCircleOutlined  } from "@ant-design/icons";
import ReportAPI from "../../../restAPI/ReportAPI";
import DocumentManagementAPI from "../../../restAPI/DocumentManagement";
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx'; // for excell file creation
import JSZip from "jszip";

const Context = React.createContext({
  name: "Default",
});

const StepForm = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { Dragger } = Upload;
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [shareDetails, setShareDetails] = useState({});
  const [attachments, setAttachments] = useState([
    { name: "example.pdf", type: "pdf" },
    { name: "report.xlsx", type: "excel" },
  ]);
  const { token } = useSelector(state => state.auth);
  const [shareform] = Form.useForm(); // form instance for the form
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]); //  for store the cc List 
  const [bccEmails, setBccEmails] = useState([]); //  for store the bcc List 
  const [loading, setLoading] = useState(false);
  const [showSubmissionPopconfirm, setShowSubmissionPopconfirm] = useState(false);
  const isMultipleBuyers = useRef(false); // ref for indicating Multiple Buyers are selected
  const emailinputRef = useRef(null); // ref for clearing the input
  const cclinputRef = useRef(null); // ref for clearing the input
  const bccinputRef = useRef(null); // ref for clearing the input
  const fileTypeIcons = {
    pdf: <FilePdfOutlined className="file-icon pdf" />,
    xls: <FileExcelOutlined className="file-icon excel" />,
    xlsx: <FileExcelOutlined className="file-icon excel" />,
  };
  const removeEmail = (email) => {
    setToEmails(toEmails.filter((item) => item !== email));
    shareform.setFieldsValue({ to: "" });
  };

  const removeFromArrayState = (setArrayFn, predicate) => {
    setArrayFn(prevArray => prevArray.filter(item => !predicate(item)));
    shareform.setFieldsValue({ cc : '' });
    shareform.setFieldsValue({ bcc : '' });
  };

  const removeAttachment = (fileName) => {
    const updatedAttachments = attachments.filter((file) => file.name !== fileName);
    // Prevent removing all attachments - at least one must remain
    if (updatedAttachments.length > 0) {
      setAttachments(updatedAttachments);
    } else {
      // Show error message if trying to remove the last attachment
      api.error({
        message: "Cannot remove attachment",
        description: "At least one attachment must remain.",
        placement: "bottomLeft",
      });
    }
  };


  useEffect(() => {
    //load the initial details from the API
    if (selectedRecords.length > 0) {
      // Filter only success records for processing
      const successRecords = selectedRecords.filter(item => item.status.toLowerCase().includes('success'));
      
      if (successRecords.length > 0) {
        // check if selected invoices have multiple Buyers (only for success records)
        let distinctBuyers = [...new Set(successRecords.map(item => item.buyerName))];
        isMultipleBuyers.current = distinctBuyers.length > 1 ? true : false;
        GetInvoiceSharingDetails();
    }
    }
  }, [selectedRecords]);

  // Reset loading state when drawer is closed
  useEffect(() => {
    if (!visible) {
      setLoading(false);
      setShowSubmissionPopconfirm(false);
    }
  }, [visible]);

  const closeDrawer = () => {
    setVisible(false);
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setLoading(false); // Reset loading state
    setShowSubmissionPopconfirm(false); // Reset popup confirmation state
    shareform.setFieldsValue({ subject: '' });
    shareform.setFieldsValue({ body: '' });
    // Reset attachments to include both PDF and Excel options
    setAttachments([
      { name: "example.pdf", type: "pdf" },
      { name: "report.xlsx", type: "excel" },
    ]);
  };


  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openDrawer: (records) => {
      setVisible(true); // Open drawer
      setSelectedRecords(records);
      setLoading(false); // Reset loading state when opening
      setShowSubmissionPopconfirm(false); // Reset popup confirmation state when opening
      // Reset attachments to include both PDF and Excel options when opening
      setAttachments([
        { name: "example.pdf", type: "pdf" },
        { name: "report.xlsx", type: "excel" },
      ]);
    },
    closeDrawer: () => {
      setVisible(false); // Close drawer
    },
  }));
   const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
      api[type]({
        message: message,
        description: description,
        placement
    });
    };
  const contextValue = useMemo(
    () => ({
      name: "Ant Design",
    }),
    []
  );

  //----------------------------------------------------------------Form Methodes-------------------------------------------------------------------------------
  const handleSend = async () => {
    try {
      setLoading(true);
      setShowSubmissionPopconfirm(false);
      
      const formData = shareform.getFieldsValue();
      
      if (toEmails.length === 0 || !formData.subject || !formData.body) {
        api.error({
          message: "Validation Error",
          description: "Please fill in all required fields.",
          placement: "bottomLeft",
        });
        return;
      }

      const accessToken = token;
      const groupId = localStorage.getItem("groupId");
      const companyId = localStorage.getItem("companyId");

      // Filter only success records for PDF download
      const successRecords = selectedRecords.filter(item => item.status.toLowerCase().includes('success'));

      if (successRecords.length === 0) {
        api.error({
          message: "No Success Records for PDF",
          description: "No records with 'success' status found. PDF attachments will not be included.",
          placement: "bottomLeft",
        });
        // Continue with Excel generation only
      }

      const downloadPromises = successRecords.map(async (record) => {
        try {
    const formData = new FormData();
          formData.append("groupId", groupId);
          formData.append("companyId", companyId);
          formData.append("MOGU_UUID", record.documentMoguID);
          formData.append("TPS_Id", record.tPS_Id);

          const response = await DocumentManagementAPI.downloadDocumentWithQRCodeZip(accessToken, formData);
          const blob = new Blob([response.data], { type: "application/pdf" });
          const safeFileName = (record.invoiceNumber || "Document").replace(/[\/\\?%*:|"<>]/g, "-");
          return { success: true, fileName: `${safeFileName}.pdf`, blob };
        } catch (err) {
          console.error(`Failed to download ${record.invoiceNumber}`, err);
          return { success: false, invoiceNumber: record.invoiceNumber };
        }
      });

      const results = await Promise.all(downloadPromises);

      const zip = new JSZip();
      const failedDownloads = [];

      results.forEach(result => {
        if (result.success) {
          zip.file(result.fileName, result.blob);
        } else {
          failedDownloads.push(result.invoiceNumber);
        }
      });

      if (failedDownloads.length > 0) {
        openNotificationWithIcon(
          "warning",
          "Partial Success",
          `Failed to download: ${failedDownloads.join(", ")}`,
          "bottomLeft"
        );
      }

      if (Object.keys(zip.files).length === 0) {
        api.error({
          message: "Error",
          description: "No documents to attach.",
          placement: "bottomLeft",
        });
        setLoading(false);
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const file = new File([zipBlob], "documents.zip", { type: "application/zip" });

      const newFormData = new FormData();
      newFormData.append("bcc", bccEmails.join(','));
      newFormData.append("cc", ccEmails.join(','));
      newFormData.append("body", formData.body);
      newFormData.append("subject", formData.subject);
      
      // Check if PDF attachment is still in the attachments list before including it
      const shouldIncludePdf = attachments.some(attachment => attachment.type === "pdf");
      if (shouldIncludePdf) {
        newFormData.append("attachments", file);
      }
      
      // Check if Excel attachment is still in the attachments list before including it
      const shouldIncludeExcel = attachments.some(attachment => attachment.type === "excel");
      if (shouldIncludeExcel) {
        // Generate Excel file and add it to attachments
        const excelBlob = await getExcelfileAsBlob();
        const excelFile = new File([excelBlob], "SelectedRecords.xlsx", { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        newFormData.append("attachments", excelFile);
      }
      
      newFormData.append("to", toEmails.join(','));

      await DocumentManagementAPI.FileShareEmail(accessToken, newFormData).then((response) => {
        openNotificationWithIcon(
          "success",
          "Successful",
          `Email sent successfully!`,
          "bottomLeft"
        );
          })
          .catch((err) => {
            if (err.response) {
            openNotificationWithIcon(
              "error",
              "Failed",
              "Email sending failed",
              "bottomLeft"
            );
                } else {
            openNotificationWithIcon(
              "error",
              "Failed",
              "Email sending failed",
              "bottomLeft"
            );
          }
        })
        .finally(() => {
          setLoading(false);
        });

      closeDrawer();
      setLoading(false);
    } catch (error) {
      console.error("Error sending email:", error);
      api.error({
        message: "Error",
        description: "Failed to send email. Please try again.",
        placement: "bottomLeft",
      });
      setLoading(false);
    }
  };
  const onFinishFailed = (errorInfo) => {
    console.log('Validation Failed:', errorInfo);
  };

  const getExcelfileAsBlob = async () => {
    const headings = [
      [
        "Document Mogu ID",
        "Invoice Number",
        "Document Type",
        "Transaction Type",
        "Upload Date",
        "Supplier Name",
        "Buyer Name",
        "Status",
        "Original File Path",
        "Total Excluding Tax",
        "Total Including Tax",
        "Is Active",
        "Is Duplicate",
        "Is Tax Mismatch",
        "TPS ID"
      ],
    ];

    try {
      const rows = Array.isArray(selectedRecords) ? selectedRecords : [];
      const data = rows.map((rowValue) => [
        rowValue.documentMoguID || "",
        rowValue.invoiceNumber || "N/A",
        rowValue.documentType || "N/A",
        rowValue.transactionType || "N/A",
        rowValue.uploadDate || "N/A",
        rowValue.supplierName || "N/A",
        rowValue.buyerName || "N/A",
        rowValue.status || "N/A",
        rowValue.originalFilePath || "N/A",
        rowValue.totalExcludingTax || "0",
        rowValue.totalIncludingTax || "0",
        rowValue.isActive || "",
        rowValue.isDuplicate || "",
        rowValue.isTaxMismatch || "",
        rowValue.tPS_Id || ""
      ]);

      const worksheetData = [...headings, ...data];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Records");

      // Generate binary string and convert to Blob
      const binaryString = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
      const buffer = new ArrayBuffer(binaryString.length);
      const view = new Uint8Array(buffer);

      for (let i = 0; i < binaryString.length; i++) {
        view[i] = binaryString.charCodeAt(i) & 0xff;
      }

      return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    } catch (error) {
      console.error("Error generating Excel file:", error);
      throw error;
    }
  };


  const addEmail = async (value,property,inputRef) => {
    let extractedMailList = [];
     if(property === "to")
    {
      //EmailList
       extractedMailList = await handleExtractEmails(value);
       let updated = [...toEmails, ...extractedMailList];
       const uniqueEmails = Array.from(new Set(updated));
       setToEmails(uniqueEmails);
       shareform.setFieldsValue({ to : '' });
    }
    else if (property === "cc")
    {
      extractedMailList = await handleExtractEmails(value);
      let updated = [...ccEmails, ...extractedMailList];
      const uniqueEmails = Array.from(new Set(updated));
      setCcEmails(uniqueEmails);
      shareform.setFieldsValue({ cc : '' });
    }
    else{
      extractedMailList = await handleExtractEmails(value);
      let updated = [...bccEmails, ...extractedMailList];
      const uniqueEmails = Array.from(new Set(updated));
      setBccEmails(uniqueEmails);
      shareform.setFieldsValue({ bcc : '' });
    }

    console.log("extract mails",value,property,inputRef);
    if (inputRef.current) inputRef.current.input.value = ''; // clear input field
  }

  const validateEmail = (email) => {
  //const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const regex = /^([a-zA-Z0-9]|\.|-)+@([a-zA-Z0-9]|\.|-)+\.[a-zA-Z]+$/gm;
  return regex.test(email);
};



  const handleExtractEmails = async (emailset) => {
    const raw = emailset
      .replace(/[\n\r]/g, "")
      .split(/[,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => validateEmail(email));

    const uniqueEmails = Array.from(new Set(raw));
    return uniqueEmails
  };
  
  //-------------------------------------------------------------------End of Form methodes----------------------------------------------------------------------
  //-----------------------------------------------------------------------API Calls-------------------------------------------------------------------------------------
  const GetInvoiceSharingDetails = async () => {
    //Generate request shareList - only for success records
    const successRecords = selectedRecords.filter(item => item.status.toLowerCase().includes('success'));
    const shareList = [];
    successRecords.map((record) => {
      const shareListItem = {
        TPS_Id: record.tPS_Id,
        MOGU_UUID: record.documentMoguID
      };
      shareList.push(shareListItem);
    });

          const payload = {
            companyId: localStorage.getItem('companyId'),
      shareList: shareList
            }
    await ReportAPI.GetInvoiceSharingDetails(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), payload)
                .then((res) => {
                   if (res?.data) {
                             const documetShareDetails = {
                                   emailList: res.data.buyers,
                                   pdfFiles: res.data.pdfFiles,
            excelFiles: res.data.excelFiles
                              };
                              setShareDetails(documetShareDetails);
                              setToEmails(documetShareDetails.emailList);
                          }
                })
                .catch((err) => {
                    if (err.response) {
                        openNotificationWithIcon('error', 'No Share Details found', `${err.response.data.message}`, 'bottomLeft');
                    } else {
                        openNotificationWithIcon('error', 'No Share Details  found', `${err.message}`, 'bottomLeft');
                    }
                });
        }

  //-------------------------------------------------------------------PopUp Confirm Methode----------------------------------------------------------------
  const handleAbortSubmissionConfirm = () => {
    setTimeout(() => {
      setShowSubmissionPopconfirm(false);
    }, 3000);
  };

  const onSubmissionPopConfirm = () => {
    if (isMultipleBuyers.current) {
      setShowSubmissionPopconfirm(true); // Show the confirmation popup
    }
    else {
      handleSend();  // Directly submit if no need to confirm
    }
  }
  //--------------------------------------------------------------------End of Popup Confirm-----------------------------------------------------------------
  //------------------------------------------------------------------------End of API------------------------------------------------------------------------------------

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title={`Share Document (${selectedRecords.length} total, ${selectedRecords.filter(item => item.status.toLowerCase().includes('success')).length} for PDF)`}
        width={700}
        visible={visible}
        onClose={closeDrawer}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={closeDrawer}>
              Cancel
            </Button>
            <Popconfirm
              title={`The selected documents belong to multiple Buyers. Do you want to proceed with sharing them?`}
              open={showSubmissionPopconfirm}
              onConfirm={() => { handleSend(); }}
              onCancel={handleAbortSubmissionConfirm}
              okText="Confirm"
              cancelText="Cancel"
            >
            <Button
                disabled={loading}
              type="primary"
              size="large"
                onClick={!loading ? onSubmissionPopConfirm : null}
            >
                {loading ? "Sending..." : "Send"}
            </Button>
            </Popconfirm>
          </div>
        }
      >
        <div style={{ marginTop: 24 }}>
        <Tag bordered={false} color="geekblue" style={{fontSize:'14px'}}>Copy Multiple emails seperated by ' ; ' / Single email adresses and press Enter</Tag>
        {selectedRecords.length > selectedRecords.filter(item => item.status.toLowerCase().includes('success')).length && (
          <Tag bordered={false} color="orange" style={{fontSize:'14px', marginTop: '8px', display: 'block'}}>
            Note: Excel will include all {selectedRecords.length} selected records. PDF will only include {selectedRecords.filter(item => item.status.toLowerCase().includes('success')).length} success records.
          </Tag>
        )}
        <Form layout="vertical"
        form={shareform}
        onFinish={handleSend}
        onFinishFailed={onFinishFailed}
        >
          {/* TO Field */}
          <Space wrap>
              {toEmails.map((email) => (
                <Tag
                color="orange"
                  key={email}
                  closable
                  onClose={() => removeEmail(email)}
                  closeIcon={<CloseOutlined />}
                  style={{marginBottom:'5px', marginTop:'5px'}}
                >
                  {email}
                </Tag>
              ))}
            </Space>
          <Form.Item label="TO" name='to' 
            rules={[
              {
                validator: (_, value) =>
                  toEmails.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please add at least one email')),
              },
            ]} required>
            <Input
              placeholder="Add email"
              size="large"
              //ref={emailinputRef}
              onPressEnter={(e) => {
                e.preventDefault(); //prevent form submission on Enter
                addEmail(e.target.value,"to",emailinputRef);
              }}
            />
          </Form.Item>

          {/* CC Field */}
          <Space wrap>
              {ccEmails.length > 0 && ccEmails.map((email) => (
                <Tag
                color="purple"
                  key={email}
                  closable
                  onClose={() => removeFromArrayState(setCcEmails,item => item === email)}
                  closeIcon={<CloseOutlined />}
                  style={{marginBottom:'5px',marginTop:'5px'}}
                >
                  {email}
                </Tag>
              ))}
            </Space>
          <Form.Item label="CC" name='cc'>
            <Input size="large" placeholder="Add CC emails"  ref={cclinputRef}
              onPressEnter={(e) => {
                e.preventDefault(); //prevent form submission on Enter
                addEmail(e.target.value,"cc",cclinputRef);  
              }} />
          </Form.Item>

          {/* BCC Field */}
          <Space wrap>
              {bccEmails.length > 0 && bccEmails.map((email) => (
                <Tag
                color="magenta"
                  key={email}
                  closable
                  onClose={() => removeFromArrayState(setBccEmails,item => item === email)}
                  closeIcon={<CloseOutlined />}
                  style={{marginBottom:'5px',marginTop:'5px'}}
                >
                  {email}
                </Tag>
              ))}
            </Space>
          <Form.Item label="BCC" name='bcc'>
            <Input size="large" placeholder="Add BCC emails" ref={bccinputRef}
             onPressEnter={(e) => {
              e.preventDefault(); //prevent form submission on Enter
                addEmail(e.target.value,"bcc",bccinputRef); 
            }} />
          </Form.Item>

          {/* Subject Field */}
          <Form.Item label="Subject" name='subject'
           rules={[{required:true,message:"Email subject is required"}]}required>
            <Input size="large" placeholder="Enter subject" />
          </Form.Item>

          {/* Body Field */}
          <Form.Item label="Body" name='body'
          rules={[{required:true,message:"Email body is required"}]} required>
            <Input.TextArea placeholder="Enter your message" rows={4} />
          </Form.Item>

         
        {/* Attachments */}
        <Form.Item label="Attachments">
          <div className="attachments-container">
            {/* PDF attachment (can be removed) */}
            {attachments.some(attachment => attachment.type === "pdf") && (
              <div className="attachment-item">
                <FilePdfOutlined className="file-icon pdf" />
                <div className="file-name">PDF</div>
                <CloseCircleOutlined
                  className="remove-icon"
                  onClick={() => removeAttachment("example.pdf")}
                />
              </div>
            )}
            
            {/* Excel attachment (can be removed) */}
            {attachments.some(attachment => attachment.type === "excel") && (
              <div className="attachment-item">
                <FileExcelOutlined className="file-icon excel" />
                <div className="file-name">EXCEL</div>
                  <CloseCircleOutlined
                    className="remove-icon"
                  onClick={() => removeAttachment("report.xlsx")}
                  />
                </div>
            )}
          </div>
        </Form.Item>
        </Form>
       
        </div>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
